var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { withFieldMetadata } from '../enhancers/withFieldMetadata';
import { withEmptyFieldEditingComponent } from '../enhancers/withEmptyFieldEditingComponent';
import { DefaultEmptyFieldEditingComponentText } from './DefaultEmptyFieldEditingComponents';
import { isFieldValueEmpty } from '@sitecore-jss/sitecore-jss/layout';
export const Link = withFieldMetadata(withEmptyFieldEditingComponent(
// eslint-disable-next-line react/display-name
forwardRef((_a, ref) => {
    var { field, editable = true, showLinkTextWithChildrenPresent } = _a, otherProps = __rest(_a, ["field", "editable", "showLinkTextWithChildrenPresent"]);
    const children = otherProps.children;
    const dynamicField = field;
    if (!field || (!dynamicField.editableFirstPart && isFieldValueEmpty(dynamicField))) {
        return null;
    }
    const resultTags = [];
    // EXPERIENCE EDITOR RENDERING
    if (editable && dynamicField.editableFirstPart) {
        const markup = dynamicField.editableFirstPart + dynamicField.editableLastPart;
        // in an ideal world, we'd pre-render React children here and inject them between editableFirstPart and editableLastPart.
        // However, we cannot combine arbitrary unparsed HTML (innerHTML) based components with actual vDOM components (the children)
        // because the innerHTML is not parsed - it'd make a discontinuous vDOM. So, we'll go for the next best compromise of rendering the link field and children separately
        // as siblings. Should be "good enough" for most cases - and write your own helper if it isn't. Or bring xEditor out of 2006.
        const htmlProps = Object.assign(Object.assign({ className: 'sc-link-wrapper', dangerouslySetInnerHTML: {
                __html: markup,
            } }, otherProps), { key: 'editable' });
        // Exclude children, since 'dangerouslySetInnerHTML' and 'children' can't be set together
        // and children will be added as a sibling
        delete htmlProps.children;
        resultTags.push(React.createElement("span", Object.assign({}, htmlProps)));
        // don't render normal link tag when editing, if no children exist
        // this preserves normal-ish behavior if not using a link body (no hacks required)
        if (!children) {
            return resultTags[0];
        }
    }
    // handle link directly on field for forgetful devs
    const link = dynamicField.href
        ? field
        : dynamicField.value;
    if (!link) {
        return null;
    }
    const anchor = link.linktype !== 'anchor' && link.anchor ? `#${link.anchor}` : '';
    const querystring = link.querystring ? `?${link.querystring}` : '';
    const anchorAttrs = {
        href: `${link.href}${querystring}${anchor}`,
        className: link.class,
        title: link.title,
        target: link.target,
    };
    if (anchorAttrs.target === '_blank' && !anchorAttrs.rel) {
        // information disclosure attack prevention keeps target blank site from getting ref to window.opener
        anchorAttrs.rel = 'noopener noreferrer';
    }
    const linkText = showLinkTextWithChildrenPresent || !children ? link.text || link.href : null;
    resultTags.push(React.createElement('a', Object.assign(Object.assign(Object.assign({}, anchorAttrs), otherProps), { key: 'link', ref }), linkText, children));
    return React.createElement(React.Fragment, null, resultTags);
}), { defaultEmptyFieldEditingComponent: DefaultEmptyFieldEditingComponentText, isForwardRef: true }), true);
export const LinkPropTypes = {
    field: PropTypes.oneOfType([
        PropTypes.shape({
            href: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf([null]).isRequired]),
        }),
        PropTypes.shape({
            value: PropTypes.object,
            editableFirstPart: PropTypes.string,
            editableLastPart: PropTypes.string,
        }),
    ]).isRequired,
    editable: PropTypes.bool,
    showLinkTextWithChildrenPresent: PropTypes.bool,
    emptyFieldEditingComponent: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func,
    ]),
};
Link.propTypes = LinkPropTypes;
Link.displayName = 'Link';
